import { Box } from "@mui/material";
import { listCaseRunsOptions } from "@platform/api/golden";
import { Card, Link } from "@platform/design-system";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { useCaseRun } from "../run/runQueries";
import { Fact, factsSx, RunFacts } from "../run/RunSummary";
import { useRunStream } from "../run/useRunStream";

import { SectionStatus } from "./SectionStatus";

import type { ReactNode } from "react";

export const CASE_LATEST_RUN_TEST_ID = "golden-case-latest-run";

const LatestRunFacts = ({
  caseId,
  runId,
}: {
  readonly caseId: number;
  readonly runId: number;
}): ReactNode => {
  const detail = useCaseRun(caseId, runId);
  // The estimated cost is priced from the run's steps, which only its stream carries.
  const stream = useRunStream(caseId, runId, () => void detail.refetch());

  return detail.isSuccess ? (
    <Box
      component="dl"
      data-testid={`${CASE_LATEST_RUN_TEST_ID}-facts`}
      sx={factsSx}
    >
      <Fact term="Run">
        <Link component={RouterLink} to={`/cases/${caseId}/runs/${runId}`}>
          #{runId}
        </Link>{" "}
        ({detail.data.run.state})
      </Fact>
      <RunFacts detail={detail.data} stream={stream} />
    </Box>
  ) : (
    <SectionStatus
      testId={CASE_LATEST_RUN_TEST_ID}
      label="latest run"
      error={detail.error}
    />
  );
};

/** The case's newest run at a glance; a case with no runs shows nothing here. */
export const CaseLatestRun = ({
  caseId,
}: {
  readonly caseId: number;
}): ReactNode => {
  const client = useGoldenClient();
  // The Runs section polls this same query while a run is unfinished.
  const runs = useQuery(
    listCaseRunsOptions({ client, path: { case_id: caseId } }),
  );
  const latest = runs.data?.[0];
  if (!latest) return null;

  return (
    <Card data-testid={CASE_LATEST_RUN_TEST_ID} title="Latest run">
      {/* Keyed by run, so a new run opens its own stream. */}
      <LatestRunFacts key={latest.id} caseId={caseId} runId={latest.id} />
    </Card>
  );
};
